import React from 'react';
import { Activity, Euro, Calculator, Building, FileText } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

const LimitsTab = ({ item, onUpdate, themeColors }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Gesamtlimit */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Activity className="w-4 h-4 mr-1.5" />
          {t('banf.limits.totalLimit')}
        </label>
        <div className="relative">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
          <input
            type="text"
            value={item.total_limit}
            onChange={(e) => onUpdate(item.id, 'total_limit', e.target.value)}
            className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
            placeholder={t('banf.limits.totalLimitPlaceholder')}
          />
        </div>
      </div>

      {/* Erwarteter Wert */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Euro className="w-4 h-4 mr-1.5" />
          {t('banf.limits.expectedValue')}
        </label>
        <div className="relative">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
          <input
            type="number"
            min="0"
            step="0.01"
            value={item.expected_value}
            onChange={(e) => onUpdate(item.id, 'expected_value', e.target.value)}
            className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            placeholder="0.00"
          />
        </div>
        {item.expected_value && (
          <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'} mt-1`}>
            {t('banf.limits.expectedValueHint')}
          </p>
        )}
      </div>

      {/* Sachkonto (Limits) */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Calculator className="w-4 h-4 mr-1.5" />
          {t('banf.limits.glAccount')}
        </label>
        <div className="relative">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
          <input
            type="text"
            value={item.limits_gl_account}
            onChange={(e) => onUpdate(item.id, 'limits_gl_account', e.target.value)}
            className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
            placeholder={t('banf.limits.glAccountPlaceholder')}
          />
        </div>
      </div>

      {/* Kostenstelle - immer anzeigen */}
      <div>
        <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
          <Building className="w-4 h-4 mr-1.5" />
          {t('banf.limits.costCenter')}
        </label>
        <div className="relative">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
          <input
            type="text"
            value={item.limits_cost_center}
            onChange={(e) => onUpdate(item.id, 'limits_cost_center', e.target.value)}
            className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
            placeholder={t('banf.limits.costCenterPlaceholder')}
          />
        </div>
      </div>

      {/* Auftrag - nur anzeigen wenn kontierungstyp === 'auftrag' */}
      {item.kontierungstyp === 'auftrag' && (
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-gray-700'} mb-2 flex items-center drop-shadow-sm`}>
            <FileText className="w-4 h-4 mr-1.5" />
            {t('banf.limits.order')}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30' : 'bg-white/50 border border-blue-200/40'} backdrop-blur-xl rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]`}></div>
            <input
              type="text"
              value={item.auftrag || ''}
              onChange={(e) => onUpdate(item.id, 'auftrag', e.target.value)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${themeColors.focusRing} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-gray-800 placeholder-gray-400'}`}
              placeholder={t('banf.limits.orderPlaceholder')}
              required
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LimitsTab;